// SettingsMembersPane.jsx

import React from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Components
import Button from 'components/button/Button';
import ProfilePhoto from 'components/profilephoto/ProfilePhoto';
import SelectInput from 'components/selectinput/SelectInput';
import { buildWorkspaceMemberProfile, getWorkspaceMemberDisplayName } from 'utilities/WorkspaceMembers';

const ROLE_OWNER = 'owner';
const ROLE_ADMIN = 'admin';
const ROLE_MEMBER = 'member';

/**
 * Normalizes an incoming member role to a known role key.
 *
 * @param {string} value - Raw role string.
 * @returns {string} Normalized role key.
 */
function normalizeRole(value) {
    const role = String(value || '').trim().toLowerCase();
    if (role === ROLE_OWNER || role === ROLE_ADMIN || role === ROLE_MEMBER) return role;
    return ROLE_MEMBER;
}

/**
 * SettingsMembersPane
 *
 * Renders space member controls, including invites, role changes, and removals.
 *
 * @param {object} theme - Active theme tokens for dynamic styling.
 * @param {string} subtitle - Summary copy for the members pane header.
 * @param {boolean} canInviteMembers - Whether the current user can invite new members.
 * @param {function} onInviteClick - Callback for invite action.
 * @param {object} workspace - Active workspace model.
 * @param {string} errorMessage - Current members error message.
 * @param {string} statusMessage - Current members status message.
 * @param {Array<object>} sortedMembers - Ordered space members.
 * @param {object} user - Current signed-in user.
 * @param {function} canManageTarget - Permission guard for a target member and action.
 * @param {function} handleRoleChange - Role update handler.
 * @param {function} handleRemoveMember - Member removal handler.
 *
 * @returns {ReactNode} The rendered members settings pane.
 */
const SettingsMembersPane = React.memo(({
    theme,
    subtitle,
    canInviteMembers,
    onInviteClick,
    workspace,
    errorMessage,
    statusMessage,
    sortedMembers,
    user,
    canManageTarget,
    handleRoleChange,
    handleRemoveMember,
}) => {

    // ----- DEVICE DETECTION ----- //

    const isMobile = useIsMobile();

    // ----- HANDLERS ----- //

    /**
     * Opens invite flow for the active workspace.
     */
    const handleInviteClick = () => {
        onInviteClick && onInviteClick(workspace);
    };

    // ----- RENDER ----- //

    return (
        <>
            <div className="settings-modal-pane-header"
                style={{
                    padding: isMobile ? '6px' : '0px',
                    paddingRight: 20
                }}
            >

                <div className="settings-modal-pane-header-left">
                    <div className="settings-modal-pane-title" style={{ color: theme.foreground }}>
                        Members
                    </div>
                    <div className="settings-modal-pane-subtitle" style={{ color: theme.foregroundMuted }}>
                        {subtitle}
                    </div>
                </div>

                <Button
                    iconKey="person_add"
                    iconSize={16}
                    label="Invite Members"
                    onClick={handleInviteClick}
                    enabled={canInviteMembers}
                    height="32px"
                    width="auto"
                    borderRadius="6px"
                    fontSize="12px"
                />
            </div>

            <div className="settings-modal-pane-contents">
                {!canInviteMembers && (
                    <div className="settings-modal-note" style={{ color: theme.foregroundMuted }}>
                        You can view members. Only admins and owners can invite or remove members.
                    </div>
                )}

                {errorMessage && (
                    <div className="settings-modal-error">{errorMessage}</div>
                )}

                {statusMessage && !errorMessage && (
                    <div className="settings-modal-status">{statusMessage}</div>
                )}

                <div className="settings-modal-members-list">
                    {sortedMembers.map((member) => {
                        const memberProfile = buildWorkspaceMemberProfile(member);
                        const key = memberProfile.userKey || memberProfile.key;
                        const role = normalizeRole(member?.role);
                        const isSelf = key === user?.key;
                        const displayName = getWorkspaceMemberDisplayName(member);

                        return (
                            <div key={key} className="settings-modal-member-row"
                                style={{
                                    flexDirection: isMobile ? 'column' : 'row',
                                    alignItems: isMobile ? 'flex-start' : 'center',
                                    marginBottom: isMobile ? '12px' : '8px',
                                }}>
                                <div className="settings-modal-member-info">
                                    <ProfilePhoto
                                        node={memberProfile}
                                        size={32}
                                        borderWidth={0}
                                    />

                                    <div className="settings-modal-member-meta">
                                        <div
                                            className="settings-modal-setting-label"
                                            style={{ color: theme.foreground }}
                                        >
                                            {displayName}
                                        </div>
                                        <div
                                            className="settings-modal-setting-description"
                                            style={{ color: theme.foregroundMuted }}
                                        >
                                            {member.email || key} • {role}
                                        </div>
                                    </div>
                                </div>

                                <div className="settings-modal-member-actions"
                                    style={{
                                        paddingLeft: isMobile ? '40px' : '0px',
                                    }}
                                >
                                    <SelectInput
                                        value={role}
                                        onChange={(event) => handleRoleChange(member, event.target.value)}
                                        style={{ 
                                            minWidth: 100, 
                                            height: '40px',
                                            borderRadius: '5px',
                                        }}
                                        disabled={!canManageTarget(member, 'changeRole')}
                                    >
                                        {role === ROLE_OWNER && <option value={ROLE_OWNER}>owner</option>}
                                        <option value={ROLE_ADMIN}>admin</option>
                                        <option value={ROLE_MEMBER}>member</option>
                                    </SelectInput>

                                    <Button
                                        label={isSelf ? 'You' : 'Remove'}
                                        onClick={() => handleRemoveMember(member)}
                                        enabled={!isSelf && canManageTarget(member, 'remove')}
                                        height="40px"
                                        fontSize="12px"
                                        borderRadius="6px"
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </>
    );
});

// Dev Tools
SettingsMembersPane.displayName = 'SettingsMembersPane';

export default SettingsMembersPane;
